/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { isEmpty, merge, omit } from 'lodash';

// Components
import ExternalPicker from './ExternalPicker.vue';
import BaseControl from 'shared/ui/components/form-components/BaseControl.vue';
import ModalForm from 'shared/ui/components/form-components/ModalForm.vue';

const __vue_script__ = {
    name: 'GenericDocExternalPicker',

    components: { ExternalPicker },

    extends: BaseControl,

    props: {
        value: {
            type: [Object, Array, String],
            default: () => []
        },

        eventId: {
            type: String,
            required: true
        },

        fpType: {
            type: String,
            required: true
        },

        sortable: {
            type: Boolean,
            default: true
        },

        filterBy: {
            type: Object,
            default: () => {}
        },

        appscript: {
            type: String,
            default: null
        },

        appscriptExtraParams: {
            type: Object,
            default: () => ({}),
        },

        showCreateButton: {
            type: Boolean,
            default: false
        },

        showDeletedDocs: {
            type: Boolean,
            default: false
        },

        timezone: {
            type: String,
            default: undefined
        },

        max: {
            type: Number,
            default: undefined
        },

        emitKey: {
            type: String,
            default: 'fp_ext_id'
        },

        valueKey: {
            type: String,
            default: 'fp_ext_ids'
        },

        excludeKey: {
            type: String,
            default: 'excludeFpExtIds'
        },

        emitObjects: {
            type: Boolean,
            default: false
        },

        exclusions: {
            type: Array,
            default: () => []
        },

        errors: {
            type: Array,
            default: () => []
        },

        multiple: {
            type: Boolean,
            default: false
        },

        edit: {
            type: Boolean,
            default: false
        },

        options: {
            type: Object,
            default: () => ({})
        },

        locale: {
            type: String,
            default: 'en'
        }
    },

    data() {
        return {
            ITEMS_PER_PAGE: 100,
            attrs: {},
            documents: [],
            hasAssetFpType: false,
            loading: false,
            searchTerm: '',
            selectedDocs: [],
            selectedDocsLoaded: false
        };
    },

    computed: {
        buttonText() {
            return this.$i18n.t(`general.${this.hasAssetFpType ? 'upload' : 'create'}`);
        },

        innerValue: {
            get() {
                return this.getValue();
            },

            set(value) {
                this.$emit('input', value);
            }
        },

        valueIds() {
            const isValueAnArray = Array.isArray(this.value);
            let ids;
            if (isValueAnArray) {
                if (this.value[0] instanceof Object) {
                    ids = this.value.map(v => v[this.emitKey]);
                } else {
                    // Must be an array of strings
                    ids = this.value;
                }
            } else {
                ids = this.value instanceof Object ? [this.value[this.emitKey]] : [this.value];
            }

            return (ids || []).filter(Boolean);
        }
    },

    watch: {
        $attrs: {
            immediate: true,
            handler() {
                this.attrs = omit({ ...this.$options.propsData, ...this.$attrs }, ['showDeletedDocs', 'event', 'legacy', 'valueKey', 'model', 'edit']);
            }
        },

        fpType() {
            this.loadDocs();
        },

        value: {
            immediate: true,
            deep: true,
            handler() {
                // We need to reload the documents on every external modification as we do not know which version is the
                // last one, the one provided to this component or the last one loaded in this component.
                this.loadSelectedDocs();
            }
        }
    },

    async created() {
        this.hasAssetFpType = await this.$services.metadata.isAssetFpType(this.eventId, this.fpType);
    },

    methods: {
        getValue() {
            try {
                if (isEmpty(this.value)) {
                    return [];
                }

                const docs = (Array.isArray(this.value) ? this.value : [this.value]).filter(Boolean);
                for (const [index, doc] of docs.entries()) {
                    const id = typeof doc === 'string' ? doc : doc[this.emitKey];
                    const selection = [...this.documents, ...this.selectedDocs].find(s => s[this.emitKey] === id);

                    if (selection) {
                        if (doc instanceof Object) {
                            delete doc.badge;
                            delete doc.badge_classes;

                            merge(doc, selection);
                        }
                        else docs[index] = selection;

                    } else if (this.showDeletedDocs) {
                        docs[index] = {
                            _id: id,
                            [this.emitKey]: id,
                            label: id,
                            badge: this.$i18n.t('general.deleted'),
                            badge_classes: 'badge-danger'
                        };
                    }
                }

                return docs;
            } catch (error) {
                console.error('[GenericDocExternalPicker] Could not load selected documents', error.message || error);
            }
        },

        async loadSelectedDocs() {
            try {
                if (this.loading || !this.valueIds.length) {
                    return;
                }

                this.loading = true;
                let selectedDocs = [];
                if (this.emitKey === 'fp_asset') {
                    // we cannot get doc by fp_asset, we need to use the ids
                    const ids = this.valueIds.map(id => id.replace(/\/file\.(png|jpeg|jpg|gif)$/i, ''));
                    selectedDocs = await this.$services.documents.getDocsByType(this.eventId, this.fpType, { ids, limit: this.ITEMS_PER_PAGE });
                } else if (this.appscript) {
                    const params = { ...this.appscriptExtraParams, ids: this.valueIds };
                    selectedDocs = await this.$services.appScripts.callAppScript(this.eventId, this.appscript, params);
                } else {
                    selectedDocs = await this.$services.documents.getDocsByType(this.eventId, this.fpType, { [this.valueKey]: this.valueIds, limit: this.ITEMS_PER_PAGE });
                }

                this.normalizeAssets(selectedDocs);
                this.normalizeLabels(selectedDocs);

                this.selectedDocs = selectedDocs;
            } catch (error) {
                console.error('[GenericDocExternalPicker] Could not load selected documents', error.message || error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Loads the documents
         */
        async loadDocs() {
            try {
                if (this.loading) {
                    return;
                }

                this.loading = true;
                let documents;

                // This make sure that we don't lose the reference to doc that haven't been loaded through selected doc and are only in this.documents
                // Otherwise, they will be gone from the next batch because they're excluded through valueIds
                for (const id of this.valueIds) {
                    if (this.selectedDocs.every(selectedDoc => selectedDoc[this.emitKey] !== id)) {
                        const doc = this.documents.find(dropDownDocs => dropDownDocs[this.emitKey] === id);
                        if (doc) {
                            this.selectedDocs.push(doc);
                        }
                    }
                }

                if (this.appscript) {
                    const params = {
                        ...this.appscriptExtraParams,
                        searchTerm: this.searchTerm,
                        excludedFpExtIds: [...this.valueIds, ...this.exclusions],
                        limit: 25
                    };
                    documents = await this.$services.appScripts.callAppScript(this.eventId, this.appscript, params);
                } else {
                    documents = await this.$services.documents.getDocsByType(this.eventId, this.fpType, {
                        search: this.searchTerm,
                        filterBy: this.filterBy,
                        [this.excludeKey]: [...this.valueIds, ...this.exclusions],
                        limit: this.ITEMS_PER_PAGE
                    });
                }

                this.normalizeAssets(documents);
                this.normalizeLabels(documents);

                this.documents = documents;
            } catch (error) {
                console.error('[GenericDocExternalPicker] Could not load documents', error.message || error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Performs the remote search
         *
         * @param {string} query the search term
         */
        search(query) {
            this.searchTerm = query;
            this.loadDocs();
        },

        /**
         * Normalizes the given documents by processing their assets.
         *
         * @param {Array} docs - The array of documents to be normalized.
         */
        normalizeAssets(docs) {
            if (this.emitKey === 'fp_asset') {
                docs.forEach(doc => doc.fp_asset = doc.fp_asset || `${doc._id}/file.png`);
            }
        },

        /**
         * Normalizes the labels of the provided documents.
         *
         * @param {Array} docs - The array of documents to normalize.
         */
        async normalizeLabels(docs) {
            try {
                for (const doc of docs) {
                    const key = doc.label || doc[this.emitKey];
                    const trLabel = this.$utils.locales.translateWithContext(key, doc, this.$i18n);
                    const __tr_label = await this.$services.translations.workspaceTranslation(this.eventId, trLabel);
                    // the label string can be an object key in the i18n file, like "session", so we need to check if it's an object
                    doc.__tr_label = (typeof __tr_label === 'object') ? trLabel : __tr_label;
                }

                docs.forEach(doc => {
                    if (docs.some(d => d._id !== doc._id && d.__tr_label === doc.__tr_label)) {
                        doc.label = `${doc.__tr_label} (${doc._id})`;
                    } else if (typeof doc.__tr_label === 'string') {
                        doc.label = doc.__tr_label;
                    }
                });

                docs.sort((a, b) => {
                    if (a.timestamp && b.timestamp) {
                        return a.timestamp - b.timestamp;
                    } else {
                        return a.label.localeCompare(b.label);
                    }
                });

            } catch (error) {
                console.error('[GenericDocExternalPicker] Could not normalize labels', error.message || error);
            }
        },

        async createNewExternal() {
            const event = await this.$services.event.getEventCached(this.eventId);
            const metadata = await this.$services.metadata.getMetadataForType(this.eventId, this.fpType);

            if (this.hasAssetFpType) {
                // Not implemented yet
                console.warn('[GenericDocExternalPicker] Asset creation not implemented yet');

            } else {
                this.$modal.show(ModalForm, {
                    title: this.$t('generic_form.external_picker.new_external', [this.$utils.strings.humanizeType(metadata, this.fpType)]),
                    event,
                    model: {},
                    fpType: this.fpType,
                    onSave: () => {
                        this.loadDocs();
                        // not doing anything more for now, because sometimes we would need id to pop up, and sometimes fp_ext_id, and that's way to complex for such a use case.
                    },
                }, {
                    classes: 'large',
                    height: 'auto',
                    clickToClose: false
                });
            }
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('external-picker',_vm._b({attrs:{"options":_vm.documents,"fp-type":_vm.fpType,"sortable":_vm.sortable,"timezone":_vm.timezone,"loading":_vm.loading,"locale":_vm.locale,"emit-key":_vm.emitKey,"errors":_vm.errors,"translate-labels":false},on:{"search":_vm.search,"open":_vm.loadDocs,"select":function($event){return _vm.$emit('select', $event)},"clean":function($event){return _vm.$emit('clean', $event)}},scopedSlots:_vm._u([{key:"option",fn:function(ref){
var option = ref.option;
return [_vm._t("option",null,{"option":option})]}},{key:"details",fn:function(ref){
var doc = ref.doc;
var remove = ref.remove;
var index = ref.index;
return [_vm._t("details",null,{"doc":doc,"remove":remove,"index":index})]}},{key:"document-item-subtitle",fn:function(ref){
var doc = ref.doc;
return [_vm._t("document-item-subtitle",null,{"doc":doc})]}},{key:"document",fn:function(ref){
var doc = ref.doc;
return [_vm._t("document",null,{"doc":doc})]}},{key:"doc-actions",fn:function(ref){
var doc = ref.doc;
return [_vm._t("doc-actions",null,{"doc":doc})]}},{key:"control-actions",fn:function(ref){return [_vm._t("custom-actions"),(_vm.showCreateButton && !_vm.hasAssetFpType)?_c('button',{staticClass:"btn btn-mini",attrs:{"type":"button"},on:{"click":_vm.createNewExternal}},[_vm._v(_vm._s(_vm.buttonText))]):_vm._e()]}}],null,true),model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}},'external-picker',_vm.attrs,false))}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "GenericDocExternalPicker.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__